import { NativePrinter } from "@/interfaces";

export interface PrinterSettingsResponse {
  selected: string | null;
  barcodeSelected: string | null;
  kotSelected: string | null;
  default: string | null;
  effective: string | null;
}

export interface SavePrinterResponse {
  success: boolean;
  message: string;
}

export interface PrintResult {
  success: boolean;
  message: string;
}

export interface CustomPageSize {
  width: number;  // Width in mm
  height: number; // Height in mm
}

declare global {
  interface Window {
    electron?: {
      isElectron: boolean;
      getPrinters: () => Promise<NativePrinter[]>;
      getPrinterSettings: () => Promise<PrinterSettingsResponse>;
      saveSelectedPrinter: (name: string) => Promise<SavePrinterResponse>;
      saveSelectedBarcodePrinter: (name: string) => Promise<SavePrinterResponse>;
      saveSelectedKotPrinter: (name: string) => Promise<SavePrinterResponse>;
      printReceipt: (payload: {
        html: string;
        title?: string;
        paperSize?: string;
        customPageSize?: CustomPageSize;
        printerName?: string;
      }) => Promise<PrintResult>;
      minimizeWindow?: () => void;
      maximizeWindow?: () => void;
      closeWindow?: () => void;
    };
  }
}

export const electronService = {
  isElectron: (): boolean => {
    return !!window.electron?.isElectron;
  },

  getPrinters: async (): Promise<NativePrinter[]> => {
    if (window.electron && window.electron.getPrinters) {
      try {
        return await window.electron.getPrinters();
      } catch (error) {
        console.error("Error fetching printers from Electron:", error);
        return [];
      }
    }
    // Browser fallback
    return [];
  },

  getPrinterSettings: async (): Promise<PrinterSettingsResponse> => {
    if (window.electron && window.electron.getPrinterSettings) {
      try {
        return await window.electron.getPrinterSettings();
      } catch (error) {
        console.error("Error fetching printer settings:", error);
      }
    }
    return { selected: null, barcodeSelected: null, default: null, effective: null };
  },

  saveSelectedPrinter: async (name: string): Promise<SavePrinterResponse> => {
    if (window.electron && window.electron.saveSelectedPrinter) {
      try {
        return await window.electron.saveSelectedPrinter(name);
      } catch (error) {
        console.error("Error saving printer:", error);
        return { success: false, message: "Internal error saving printer." };
      }
    }
    return { success: false, message: "Browser mode does not support native printers." };
  },

  saveSelectedBarcodePrinter: async (name: string): Promise<SavePrinterResponse> => {
    return new Promise((resolve) => {
      if (!window.electron?.saveSelectedBarcodePrinter) {
        resolve({ success: false, message: "Not running in desktop app." });
        return;
      }
      window.electron.saveSelectedBarcodePrinter(name)
        .then(resolve)
        .catch(error => resolve({ success: false, message: error.message }));
    });
  },

  saveSelectedKotPrinter: async (name: string): Promise<SavePrinterResponse> => {
    return new Promise((resolve) => {
      if (!window.electron?.saveSelectedKotPrinter) {
        resolve({ success: false, message: "Not running in desktop app." });
        return;
      }
      window.electron.saveSelectedKotPrinter(name)
        .then(resolve)
        .catch(error => resolve({ success: false, message: error.message }));
    });
  },

  getEffectivePrinter: async (): Promise<string | null> => {
    const settings = await electronService.getPrinterSettings();
    return settings.effective;
  },

  printReceipt: async (
    html: string,
    title?: string,
    paperSize?: string,
    printerName?: string,
    customPageSize?: CustomPageSize
  ): Promise<PrintResult> => {
    if (electronService.isElectron() && window.electron?.printReceipt) {
      try {
        return await window.electron.printReceipt({
          html,
          title,
          paperSize,
          customPageSize,
          printerName,
        });
      } catch (error: any) {
        console.error("Error printing receipt in Electron:", error);
        return { success: false, message: `Electron print error: ${error.message}` };
      }
    }

    // Browser Fallback via Hidden IFrame
    return new Promise((resolve) => {
      try {
        const iframe = document.createElement("iframe");
        iframe.style.position = "absolute";
        iframe.style.width = "0px";
        iframe.style.height = "0px";
        iframe.style.border = "none";
        
        document.body.appendChild(iframe);
        
        const doc = iframe.contentWindow?.document;
        if (!doc) {
          throw new Error("Iframe document not found");
        }
        
        doc.open();
        doc.write(html);
        doc.close();

        iframe.onload = () => {
          setTimeout(() => {
            try {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
              resolve({ success: true, message: "Browser print triggered successfully." });
            } catch (err: any) {
              resolve({ success: false, message: `Browser print failed: ${err.message}` });
            } finally {
              // Clean up iframe after print dialog is closed or fails
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              }, 1000);
            }
          }, 500); // small delay to ensure fonts and layout apply within the iframe
        };
      } catch (error: any) {
        console.error("Error printing receipt in Browser:", error);
        resolve({ success: false, message: `Browser print error: ${error.message}` });
      }
    });
  },

  minimizeWindow: () => {
    if (window.electron?.minimizeWindow) {
      window.electron.minimizeWindow();
    }
  },

  maximizeWindow: () => {
    if (window.electron?.maximizeWindow) {
      window.electron.maximizeWindow();
    }
  },

  closeWindow: () => {
    if (window.electron?.closeWindow) {
      window.electron.closeWindow();
    }
  }
};
